import React, { useState, useEffect } from 'react'
import { X, Clock, Users, ChefHat, Plus, Minus, Check, Timer, ChevronLeft, ChevronRight, Star, ShoppingCart, Maximize2, Minimize2, ExternalLink } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { RecipeShoppingList } from '../recipes/RecipeShoppingList'

interface Recipe {
  id: string
  name: string
  description?: string
  photo_url?: string
  meal_type?: string
  prep_time_minutes?: number
  cook_time_minutes?: number
  total_time_minutes?: number
  servings: number
  difficulty: string
  avg_rating?: number
  review_count?: number
  ingredients: Ingredient[]
  instructions: Instruction[]
  nutrition?: NutritionInfo
  tags?: string[]
  source_url?: string
  source_type?: string
}

interface Ingredient {
  id: string
  ingredient_name: string
  quantity?: number
  quantity_display?: string
  unit?: string
  preparation_notes?: string
  ingredient_group?: string
  is_optional: boolean
}

interface Instruction {
  id: string
  step_number: number
  instruction: string
  time_minutes?: number
  photo_url?: string
}

interface NutritionInfo {
  calories?: number
  protein_g?: number
  carbs_g?: number
  fat_g?: number
}

interface KioskRecipeViewerProps {
  recipeId?: string
  recipe?: Recipe
  onClose: () => void
  fullScreen?: boolean
}

export default function KioskRecipeViewer({ recipeId, recipe: propRecipe, onClose, fullScreen = false }: KioskRecipeViewerProps) {
  const [recipe, setRecipe] = useState<Recipe | null>(propRecipe || null)
  const [loading, setLoading] = useState(!propRecipe)
  const [servings, setServings] = useState(4)
  const [servingIncrement, setServingIncrement] = useState(1)
  const [currentStep, setCurrentStep] = useState(0)
  const [cookingMode, setCookingMode] = useState(false)
  const [checkedIngredients, setCheckedIngredients] = useState<Set<string>>(new Set())
  const [activeTimers, setActiveTimers] = useState<{ [key: number]: number }>({})
  const [showShoppingList, setShowShoppingList] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(fullScreen)

  // Fullscreen API functions
  const enterFullscreen = () => {
    const elem = document.documentElement
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if ((elem as any).webkitRequestFullscreen) {
      (elem as any).webkitRequestFullscreen()
    } else if ((elem as any).mozRequestFullScreen) {
      (elem as any).mozRequestFullScreen()
    } else if ((elem as any).msRequestFullscreen) {
      (elem as any).msRequestFullscreen()
    }
    setIsFullscreen(true)
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen()
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen()
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen()
    }
    setIsFullscreen(false)
  }

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen()
    } else {
      enterFullscreen()
    }
  }

  // Helper function to proxy external recipe images
  const getProxiedImageUrl = (url: string | undefined) => {
    if (!url) return ''

    // Check if URL is external (needs proxying)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // Only proxy if it's not already our own host
      let sameHost = false
      try {
        const host = new URL(url).hostname
        sameHost = host === window.location.hostname || host === 'localhost'
      } catch {
        sameHost = false
      }
      if (!sameHost) {
        return `/api/kiosk/recipe-image-proxy?url=${encodeURIComponent(url)}`
      }
    }
    return url
  }

  useEffect(() => {
    if (recipeId && !propRecipe) {
      loadRecipe()
    }
  }, [recipeId, propRecipe])

  useEffect(() => {
    if (recipe) {
      setServings(recipe.servings)
      // Set smart increment based on recipe type and original servings
      const recipeName = recipe.name?.toLowerCase() || ''
      const isSoup = recipeName.includes('soup') || recipeName.includes('broth') || recipeName.includes('stew')
      const isLargeRecipe = recipe.servings >= 12
      
      if (isSoup || isLargeRecipe) {
        setServingIncrement(6) // Increment by 6 for soups and large recipes
      } else if (recipe.servings >= 8) {
        setServingIncrement(4) // Increment by 4 for medium recipes
      } else if (recipe.servings >= 4) {
        setServingIncrement(2) // Increment by 2 for small-medium recipes
      } else {
        setServingIncrement(1) // Increment by 1 for small recipes
      }
    }
  }, [recipe])

  const loadRecipe = async () => {
    if (!recipeId) return
    
    setLoading(true)
    try {
      // Use kiosk-specific endpoint for recipes
      const response = await fetch(`/api/kiosk/recipes/${recipeId}`)
      if (!response.ok) {
        throw new Error('Failed to load recipe')
      }
      const data = await response.json()
      setRecipe(data)
    } catch (error) {
      console.error('Failed to load recipe:', error)
    } finally {
      setLoading(false)
    }
  }

  const adjustQuantity = (originalQuantity: number | undefined, originalServings: number) => {
    if (!originalQuantity) return ''
    const adjusted = (originalQuantity * servings) / originalServings
    // Round to reasonable precision
    if (adjusted < 1) {
      return adjusted.toFixed(2).replace(/\.?0+$/, '')
    }
    return adjusted % 1 === 0 ? adjusted.toString() : adjusted.toFixed(1)
  }

  const toggleIngredient = (ingredientId: string) => {
    const newChecked = new Set(checkedIngredients)
    if (newChecked.has(ingredientId)) {
      newChecked.delete(ingredientId)
    } else {
      newChecked.add(ingredientId)
    }
    setCheckedIngredients(newChecked)
  }

  const startTimer = (stepNumber: number, minutes: number) => {
    setActiveTimers(prev => ({
      ...prev,
      [stepNumber]: minutes * 60
    }))

    const interval = setInterval(() => {
      setActiveTimers(prev => {
        const newTime = (prev[stepNumber] || 0) - 1
        if (newTime <= 0) {
          clearInterval(interval)
          // Play sound or notification
          new Audio('/notification.mp3').play().catch(() => {})
          return { ...prev, [stepNumber]: 0 }
        }
        return { ...prev, [stepNumber]: newTime }
      })
    }, 1000)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (!recipe) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">Recipe not found</p>
        <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg">
          Close
        </button>
      </div>
    )
  }

  const containerClass = fullScreen 
    ? 'fixed inset-0 bg-white z-50 overflow-auto' 
    : 'bg-white rounded-lg shadow-lg max-w-4xl mx-auto'

  // Wrap in modal for fullscreen
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto relative">
          {renderContent()}
        </div>
      </div>
    )
  }

  function renderContent() {
    return (
      <>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
              title="Close Recipe"
            >
              <X className="w-6 h-6" />
              <span className="text-sm font-medium">Close</span>
            </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{recipe.name}</h1>
            {recipe.avg_rating && (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.round(recipe.avg_rating || 0)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  ({recipe.review_count} reviews)
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {!cookingMode && (
            <button
              onClick={() => setShowShoppingList(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <ShoppingCart className="w-5 h-5" />
              Add to Shopping List
            </button>
          )}
          
          <button
            onClick={() => {
              setCookingMode(!cookingMode)
              if (!cookingMode) {
                setCurrentStep(0) // Start with ingredients
                enterFullscreen() // Auto-enter fullscreen when starting cooking mode
              } else {
                exitFullscreen() // Exit fullscreen when exiting cooking mode
              }
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              cookingMode 
                ? 'bg-green-500 text-white hover:bg-green-600' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {cookingMode ? 'Exit Cooking Mode' : 'Start Cooking'}
          </button>
          
          {/* Fullscreen toggle button */}
          {cookingMode && (
            <button
              onClick={toggleFullscreen}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center gap-2"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullscreen ? (
                <><Minimize2 className="w-5 h-5" /> Exit Fullscreen</>
              ) : (
                <><Maximize2 className="w-5 h-5" /> Fullscreen</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Recipe Info */}
      {!cookingMode && (
        <div className="p-6">
          {recipe.photo_url && (
            <img
              src={getProxiedImageUrl(recipe.photo_url)}
              alt={recipe.name}
              className="w-full h-64 object-cover rounded-lg mb-6"
              onError={(e) => {
                console.log('Failed to load recipe image:', recipe.photo_url)
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          
          {recipe.description && (
            <p className="text-gray-600 text-lg mb-6">{recipe.description}</p>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Prep Time</span>
              </div>
              <p className="font-bold text-lg mt-1 text-gray-900">
                {recipe.prep_time_minutes ? `${recipe.prep_time_minutes} min` : 'Not specified'}
              </p>
            </div>
            
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-700">
                <ChefHat className="w-5 h-5" />
                <span className="text-sm font-medium">Cook Time</span>
              </div>
              <p className="font-bold text-lg mt-1 text-gray-900">
                {recipe.cook_time_minutes ? `${recipe.cook_time_minutes} min` : 'Not specified'}
              </p>
            </div>
            
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Users className="w-5 h-5" />
                <span className="text-sm font-medium">Servings</span>
              </div>
              <p className="font-bold text-lg mt-1 text-gray-900">{servings} servings</p>
              {recipe.servings !== servings && (
                <p className="text-xs text-gray-500 mt-1">(Original: {recipe.servings})</p>
              )}
            </div>
            
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
              <div className="text-gray-700 text-sm font-medium">Difficulty</div>
              <p className="font-bold text-lg mt-1 capitalize text-gray-900">{recipe.difficulty || 'Not specified'}</p>
            </div>
          </div>

          {/* Tags */}
          {recipe.tags && recipe.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {recipe.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Source URL */}
          {recipe.source_url && (
            <div className="mb-6">
              <a
                href={recipe.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                View Original Recipe
                <span className="text-gray-500 text-sm">
                  ({new URL(recipe.source_url).hostname})
                </span>
              </a>
            </div>
          )}
        </div>
      )}

      {/* Cooking Mode - Full Screen Step by Step */}
      {cookingMode ? (
        <div className={`${isFullscreen ? 'fixed inset-0 bg-white z-50' : 'h-[calc(100vh-80px)]'} flex flex-col ${isFullscreen ? 'p-8' : 'p-6'}`}>
          {/* Fullscreen header with close button */}
          {isFullscreen && (
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-gray-900">{recipe.name}</h1>
              <button
                onClick={() => {
                  exitFullscreen()
                  setCookingMode(false)
                  setCurrentStep(0)
                }}
                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                title="Exit Cooking Mode"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          )}
          
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className={`${isFullscreen ? 'text-xl' : 'text-sm'} text-gray-600 font-medium`}>
                {currentStep === 0 ? 'Gather Ingredients' : `Step ${currentStep} of ${recipe.instructions.length}`}
              </span>
              <span className={`${isFullscreen ? 'text-xl' : 'text-sm'} text-gray-600`}>
                {Math.round((currentStep / (recipe.instructions.length || 1)) * 100)}% Complete
              </span>
            </div>
            <div className={`w-full bg-gray-200 rounded-full ${isFullscreen ? 'h-4' : 'h-2'}`}>
              <div 
                className={`bg-blue-500 ${isFullscreen ? 'h-4' : 'h-2'} rounded-full transition-all duration-300`}
                style={{ width: `${(currentStep / (recipe.instructions.length || 1)) * 100}%` }}
              />
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex items-center justify-center">
            {currentStep === 0 ? (
              /* Ingredients Checklist */
              <div className="w-full max-w-3xl">
                <h2 className={`${isFullscreen ? 'text-5xl' : 'text-3xl'} font-bold mb-6 text-center`}>Gather Your Ingredients</h2>
                <div className={`bg-gray-50 rounded-xl ${isFullscreen ? 'p-8' : 'p-6'} ${isFullscreen ? 'max-h-[70vh]' : 'max-h-[60vh]'} overflow-y-auto`}>
                  {recipe.ingredients && recipe.ingredients.length > 0 ? (
                    (() => {
                      // Group ingredients if they have groups
                      const grouped = recipe.ingredients.reduce((acc: any, ing: any) => {
                        const group = ing.ingredient_group || 'Main Ingredients'
                        if (!acc[group]) acc[group] = []
                        acc[group].push(ing)
                        return acc
                      }, {})
                      
                      return Object.entries(grouped).map(([group, items]: [string, any]) => (
                        <div key={group} className="mb-6 last:mb-0">
                          {Object.keys(grouped).length > 1 && (
                            <h3 className="text-lg font-semibold text-gray-700 mb-3">{group}</h3>
                          )}
                          <div className="space-y-3">
                            {items.map((ing: any) => {
                              let ingredientText = ''
                              // Only use preparation_notes as full text if there's no quantity/unit data
                              const prepNotesIsFullText = ing.preparation_notes &&
                                (!ing.ingredient_name || ing.preparation_notes.includes(ing.ingredient_name)) &&
                                !ing.quantity && !ing.unit

                              if (prepNotesIsFullText) {
                                ingredientText = ing.preparation_notes
                              } else {
                                // Use quantity_display if available, otherwise fall back to adjustQuantity
                                if (ing.quantity_display && servings === recipe.servings) {
                                  ingredientText += ing.quantity_display
                                } else if (ing.quantity) {
                                  const adjustedQty = adjustQuantity(ing.quantity, recipe.servings)
                                  if (adjustedQty) ingredientText += adjustedQty
                                }
                                if (ing.unit) {
                                  ingredientText += (ingredientText ? ' ' : '') + ing.unit
                                }
                                const name = ing.ingredient_name || ing.name || ing.ingredient || ''
                                if (name) {
                                  ingredientText += (ingredientText ? ' ' : '') + name
                                }
                                if (ing.preparation_notes && !prepNotesIsFullText) {
                                  const notes = ing.preparation_notes.trim()
                                  if (notes && !ingredientText.includes(notes)) {
                                    ingredientText += ' ' + notes
                                  }
                                }
                              }
                              
                              if (!ingredientText.trim()) {
                                ingredientText = 'Unknown ingredient'
                              }
                              
                              return (
                                <label
                                  key={ing.id}
                                  className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer hover:bg-white transition-colors ${
                                    checkedIngredients.has(ing.id) ? 'bg-green-50' : 'bg-white'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checkedIngredients.has(ing.id)}
                                    onChange={() => toggleIngredient(ing.id)}
                                    className="w-5 h-5 text-green-500"
                                  />
                                  <span className={`${isFullscreen ? 'text-2xl' : 'text-lg'} ${checkedIngredients.has(ing.id) ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                    {ingredientText}
                                    {ing.is_optional && (
                                      <span className="text-gray-500 text-base ml-2">(optional)</span>
                                    )}
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      ))
                    })()
                  ) : (
                    <div className="text-gray-500 italic text-center">
                      No ingredients available for this recipe
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Cooking Step */
              <div className="w-full max-w-4xl">
                <div className="text-center mb-8">
                  <h2 className={`${isFullscreen ? 'text-6xl' : 'text-4xl'} font-bold mb-2`}>Step {currentStep}</h2>
                  {recipe.instructions[currentStep - 1]?.time_minutes && (
                    <button
                      onClick={() => startTimer(
                        currentStep,
                        recipe.instructions[currentStep - 1].time_minutes!
                      )}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Timer className="w-5 h-5" />
                      {activeTimers[currentStep] 
                        ? formatTime(activeTimers[currentStep])
                        : `Set Timer: ${recipe.instructions[currentStep - 1].time_minutes} min`
                      }
                    </button>
                  )}
                </div>
                
                <div className={`bg-gray-50 rounded-2xl ${isFullscreen ? 'p-12' : 'p-10'}`}>
                  <p className={`${isFullscreen ? 'text-3xl' : 'text-2xl'} leading-relaxed text-gray-900`}>
                    {recipe.instructions[currentStep - 1]?.instruction}
                  </p>
                </div>
                
                {recipe.instructions[currentStep - 1]?.photo_url && (
                  <img
                    src={getProxiedImageUrl(recipe.instructions[currentStep - 1].photo_url)}
                    alt={`Step ${currentStep}`}
                    className="w-full max-w-md mx-auto mt-6 rounded-lg shadow-lg"
                    onError={(e) => {
                      console.log('Failed to load step image:', recipe.instructions[currentStep - 1].photo_url)
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 ${isFullscreen ? 'px-8 py-4' : 'px-6 py-3'} bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors ${isFullscreen ? 'text-xl' : 'text-lg'} font-medium`}
            >
              <ChevronLeft className="w-6 h-6" />
              {currentStep === 0 ? 'Back' : currentStep === 1 ? 'Ingredients' : 'Previous'}
            </button>
            
            <button
              onClick={() => {
                if (currentStep === recipe.instructions.length) {
                  exitFullscreen()
                  setCookingMode(false)
                  setCurrentStep(0)
                } else {
                  setCurrentStep(Math.min(recipe.instructions.length, currentStep + 1))
                }
              }}
              className={`flex items-center gap-2 ${isFullscreen ? 'px-8 py-4' : 'px-6 py-3'} rounded-lg transition-colors ${isFullscreen ? 'text-xl' : 'text-lg'} font-medium ${
                currentStep === recipe.instructions.length 
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {currentStep === recipe.instructions.length ? (
                <>Done! <Check className="w-6 h-6" /></>
              ) : currentStep === 0 && checkedIngredients.size === recipe.ingredients.length ? (
                <>Start Cooking <ChevronRight className="w-6 h-6" /></>
              ) : (
                <>Next <ChevronRight className="w-6 h-6" /></>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* Regular Recipe View */
        <div className="grid lg:grid-cols-2 gap-6 p-6">
          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Ingredients</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-700 font-medium">Servings:</span>
                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-1">
                  <button
                    onClick={() => setServings(Math.max(servingIncrement, servings - servingIncrement))}
                    className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-700"
                    title={`Decrease by ${servingIncrement}`}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <span className="font-semibold px-2 min-w-[2rem] text-center text-gray-900">{servings}</span>
                  <button
                    onClick={() => setServings(servings + servingIncrement)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-700"
                    title={`Increase by ${servingIncrement}`}
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
                {recipe.servings !== servings && (
                  <span className="text-xs text-gray-500">
                    (Original: {recipe.servings})
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {recipe && recipe.ingredients && Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0 ? (
                (() => {
                  // Ensure unique ingredients (deduplicate by ID)
                  const uniqueIngredients = Array.from(
                    new Map(recipe.ingredients.map(ing => [ing.id, ing])).values()
                  )
                  
                  // Group ingredients by their group
                  const groupedIngredients = uniqueIngredients.reduce((acc, ing) => {
                    const group = ing.ingredient_group || 'Main'
                    if (!acc[group]) acc[group] = []
                    acc[group].push(ing)
                    return acc
                  }, {} as Record<string, typeof recipe.ingredients>)

                  // Render grouped ingredients
                  return Object.entries(groupedIngredients).map(([group, items]) => (
                    <div key={group}>
                      {group !== 'Main' && (
                        <h3 className="font-medium text-gray-700 mt-4 mb-2">{group}</h3>
                      )}
                      {items.map(ing => {
                        // Build the ingredient text intelligently
                        let ingredientText = ''

                        // Build from individual fields - don't use preparation_notes as full text
                        // when we have quantity/unit data (to use quantity_display fractions)
                        if (ing.quantity_display && servings === recipe.servings) {
                          ingredientText += ing.quantity_display
                        } else if (ing.quantity) {
                          const adjustedQty = adjustQuantity(ing.quantity, recipe.servings)
                          if (adjustedQty) {
                            ingredientText += adjustedQty
                          }
                        }

                        if (ing.unit) {
                          ingredientText += (ingredientText ? ' ' : '') + ing.unit
                        }

                        const name = ing.ingredient_name || ing.name || ing.ingredient || ''
                        if (name) {
                          ingredientText += (ingredientText ? ' ' : '') + name
                        }

                        // Only add prep notes if they're actually additional info
                        // Check if it's different from what we already built
                        if (ing.preparation_notes) {
                          const notes = ing.preparation_notes.trim()
                          // Don't add if prep notes just duplicate the quantity/unit/name we already have
                          const hasQuantity = notes.includes(String(ing.quantity))
                          const hasUnit = ing.unit && notes.includes(ing.unit)
                          const hasName = ing.ingredient_name && notes.includes(ing.ingredient_name)
                          const isFullText = hasQuantity && hasUnit && hasName

                          if (!isFullText && notes && !ingredientText.includes(notes)) {
                            ingredientText += ' ' + notes
                          }
                        }
                        
                        // Fallback if no text was built
                        if (!ingredientText.trim()) {
                          ingredientText = 'Unknown ingredient'
                        }
                        
                        return (
                          <label
                            key={ing.id}
                            className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                              checkedIngredients.has(ing.id) ? 'opacity-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checkedIngredients.has(ing.id)}
                              onChange={() => toggleIngredient(ing.id)}
                              className="mt-1 flex-shrink-0"
                            />
                            <div className="flex-1 text-gray-900">
                              <span className={`${checkedIngredients.has(ing.id) ? 'line-through' : ''} break-words block`}>
                                {ingredientText}
                                {ing.is_optional && (
                                  <span className="text-gray-500 text-sm ml-2">(optional)</span>
                                )}
                              </span>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  ))
                })()
              ) : (
                <div className="text-gray-500 italic">
                  No ingredients available for this recipe
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Instructions</h2>
            {cookingMode ? (
              // Step-by-step mode
              <div>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">
                      Step {currentStep} of {recipe.instructions.length}
                    </span>
                    {recipe.instructions[currentStep - 1]?.time_minutes && (
                      <button
                        onClick={() => startTimer(
                          currentStep,
                          recipe.instructions[currentStep - 1].time_minutes!
                        )}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-500 text-white rounded-lg"
                      >
                        <Timer className="w-4 h-4" />
                        {activeTimers[currentStep] 
                          ? formatTime(activeTimers[currentStep])
                          : `${recipe.instructions[currentStep - 1].time_minutes} min`
                        }
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className="text-xl leading-relaxed">
                      {recipe.instructions[currentStep - 1]?.instruction}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    {currentStep === 0 ? 'Ingredients' : 'Previous'}
                  </button>
                  
                  <button
                    onClick={() => setCurrentStep(Math.min(recipe.instructions.length, currentStep + 1))}
                    disabled={currentStep === recipe.instructions.length}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                  >
                    {currentStep === recipe.instructions.length ? 'Done!' : 'Next'}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              // All steps view
              <ol className="space-y-3">
                {recipe.instructions.map(instruction => (
                  <li key={instruction.id} className="flex gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold">
                      {instruction.step_number}
                    </span>
                    <div className="flex-1">
                      <p className="text-gray-700">{instruction.instruction}</p>
                      {instruction.time_minutes && (
                        <span className="inline-flex items-center gap-1 mt-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {instruction.time_minutes} minutes
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}

      {/* Nutrition Info */}
      {recipe.nutrition && !cookingMode && (
        <div className="border-t border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-4">Nutrition Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {recipe.nutrition.calories && (
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{recipe.nutrition.calories}</p>
                <p className="text-sm text-gray-600">Calories</p>
              </div>
            )}
            {recipe.nutrition.protein_g && (
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{recipe.nutrition.protein_g}g</p>
                <p className="text-sm text-gray-600">Protein</p>
              </div>
            )}
            {recipe.nutrition.carbs_g && (
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{recipe.nutrition.carbs_g}g</p>
                <p className="text-sm text-gray-600">Carbs</p>
              </div>
            )}
            {recipe.nutrition.fat_g && (
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{recipe.nutrition.fat_g}g</p>
                <p className="text-sm text-gray-600">Fat</p>
              </div>
            )}
          </div>
        </div>
      )}
      
        {/* Shopping List Modal */}
        {showShoppingList && recipe && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <RecipeShoppingList
                recipeId={recipe.id}
                recipeName={recipe.name}
                recipeServings={recipe.servings}
                ingredients={recipe.ingredients}
                onClose={() => setShowShoppingList(false)}
              />
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className={containerClass}>
      {renderContent()}
    </div>
  )
}